/**
 * `WindowTimeline` — шкала окон работ с прямым управлением: полосу можно
 * тащить (сдвиг окна), за край — менять длительность, строку — переставлять.
 * Общий компонент: им пользуются шаблон культуры (окна без года, относительные
 * работы) и план агроцикла (окна с датами).
 *
 * Что компонент НЕ делает: не знает, что за этим окном стоит. Он считает даты
 * на шкале и сообщает вызывающему, что человек хотел (`onShift`, `onReorder`);
 * превращать это в правку срока и сохранять — дело экрана. Поэтому правки на
 * шкале сразу видны (экран перерисовывает полосы по своему черновику), а не
 * живут в самом компоненте.
 *
 * Управление:
 * - мышь/касание: тащить полосу — сдвиг; тащить край — длительность; шаг —
 *   `step` (сутки), подсказка над полосой показывает новые даты;
 * - клавиатура на полосе: ←/→ сдвиг на сутки, с Shift — на неделю, Alt+←/→ —
 *   конец окна, Enter — открыть работу, Esc во время перетаскивания — отмена;
 * - строки: ручка слева (мышью или ↑/↓ с клавиатуры) переносит работу внутри
 *   группы и между группами;
 * - работа, отсчитываемая от другой (`link`), тащится только целиком, а связь
 *   рисуется пунктиром.
 */

import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { ChevronRight, GripVertical } from 'lucide-react';
import { defineMessages } from '../../../i18n/define';
import { useT } from '../../../i18n/hooks';
import { dateDay, dateShort, monthShort } from '../../lib/format';
import { Icon } from '../Icon';
import {
  addDays,
  applyRowMove,
  buildScale,
  diffDays,
  dragWindow,
  keyboardMove,
  maxIso,
  minIso,
  pctOf,
  sameOrder,
  sameWindow,
  snapDelta,
  widthPctOf,
  type DayWindow,
  type RowMove,
  type ShiftKind,
  type TimelineStep,
} from './windowMath';
import './window-timeline.css';

const m = defineMessages({
  grip: {
    id: 'common.ui.timeline.grip',
    defaultMessage: 'Переместить «{name}»',
    description: 'aria-label ручки строки на шкале работ: перетащить мышью или стрелками вверх/вниз; {name} — название работы',
  },
  gripHelp: {
    id: 'common.ui.timeline.gripHelp',
    defaultMessage: 'Стрелки вверх и вниз переносят работу, на краю этапа — в соседний этап',
    description: 'Подсказка ручки строки на шкале работ (title и aria-description)',
  },
  barLabel: {
    id: 'common.ui.timeline.barLabel',
    defaultMessage: '{name}: {range}',
    description: 'aria-label полосы работы на шкале: название и окно дат; {name} — название работы, {range} — «20 фев – 22 мар»',
  },
  helpFree: {
    id: 'common.ui.timeline.helpFree',
    defaultMessage: 'Стрелки влево и вправо сдвигают окно на день, с Shift — на неделю. Alt и стрелки меняют конец окна. Enter открывает работу.',
    description: 'Подсказка полосы работы на шкале (aria-description), когда окно можно сдвигать и растягивать',
  },
  helpWhole: {
    id: 'common.ui.timeline.helpWhole',
    defaultMessage: 'Работа отсчитывается от другой: стрелки влево и вправо меняют смещение на день, с Shift — на неделю. Enter открывает работу.',
    description: 'Подсказка полосы работы на шкале (aria-description), когда работа привязана к другой и сдвигается только целиком',
  },
  helpOpen: {
    id: 'common.ui.timeline.helpOpen',
    defaultMessage: 'Enter открывает работу.',
    description: 'Подсказка полосы работы на шкале (aria-description), когда окно двигать нельзя',
  },
  noWindow: { id: 'common.ui.timeline.noWindow', defaultMessage: 'без окна', description: 'Подпись в дорожке шкалы у работы без окна дат' },
  range: { id: 'common.ui.timeline.range', defaultMessage: '{from} – {to}', description: 'Окно дат в подсказке над полосой шкалы: «20 фев – 22 мар»; {from}/{to} — даты' },
  rangeYear: {
    id: 'common.ui.timeline.rangeYear',
    defaultMessage: '{range} ({year})',
    description: 'Окно дат шаблона с пояснением года: «20 ноя – 10 дек (предыдущий год)»; {range} — окно, {year} — какой год',
  },
  dateYear: {
    id: 'common.ui.timeline.dateYear',
    defaultMessage: '{date} ({year})',
    description: 'Одна дата шаблона с пояснением года: «8 янв (следующий год)»; {date} — дата, {year} — какой год',
  },
  yearPrev: { id: 'common.ui.timeline.yearPrev', defaultMessage: 'предыдущий год', description: 'Пояснение даты шаблона: она относится к году до года посадки' },
  yearNext: { id: 'common.ui.timeline.yearNext', defaultMessage: 'следующий год', description: 'Пояснение даты шаблона: она относится к году после года посадки' },
  tipDelta: {
    id: 'common.ui.timeline.tipDelta',
    defaultMessage: '{range} · {delta}',
    description: 'Подсказка при перетаскивании полосы: новое окно и на сколько оно сдвинулось; {range} — «20 фев – 22 мар», {delta} — «+7 дн.»',
  },
  deltaDays: { id: 'common.ui.timeline.deltaDays', defaultMessage: '{delta} дн.', description: 'Сдвиг в сутках со знаком: «+7 дн.», «−3 дн.»; {delta} — число со знаком' },
  moved: { id: 'common.ui.timeline.moved', defaultMessage: '{name}: {range}', description: 'Объявление для чтения с экрана после сдвига окна клавишами; {name} — работа, {range} — новое окно' },
  reordered: {
    id: 'common.ui.timeline.reordered',
    defaultMessage: '{name}: {group}, место {position} из {total}',
    description: 'Объявление для чтения с экрана после переноса строки; {name} — работа, {group} — этап, {position} и {total} — числа',
  },
});

/* -------------------------------------------------------------------- типы */

/** Как полосу можно двигать: `free` — целиком и за края; `whole` — только целиком; `fixed` — нельзя. */
export type BarDrag = 'free' | 'whole' | 'fixed';

export interface TimelineBar {
  id: string;
  label: string;
  /** Начало окна, ISO; `null` — у работы нет окна (строка есть, полосы нет). */
  from: string | null;
  /** Конец окна; `null` — метка в одну дату. */
  to: string | null;
  drag?: BarDrag;
  /** Примерный срок: полоса штрихуется. */
  approximate?: boolean;
  /** Работа по желанию: полоса полупрозрачная. */
  optional?: boolean;
  /** Правка ещё не сохранена: полоса и строка помечены. */
  changed?: boolean;
  /** Строка помечена на удаление: полосы нет, текст приглушён. */
  removed?: boolean;
  tone?: 'default' | 'ok' | 'muted';
  /** Пунктирная связь с работой, от которой отсчитывается окно. */
  link?: { toId: string; edge: 'start' | 'end' };
  /** Вторая строка подсказки над полосой (например, «через 3 дн. после начала «Сев»»). */
  hint?: string;
}

export interface TimelineGroup {
  id: string;
  label: string;
  /** Короткая подпись справа от названия («6 работ»). */
  summary?: ReactNode;
  bars: TimelineBar[];
  /** Текст вместо строк у раскрытой пустой группы. */
  empty?: ReactNode;
}

export interface ShiftInfo {
  kind: ShiftKind;
  /** На сколько суток сдвинулся край (для `move` и `start` — начало, для `end` — конец). */
  deltaDays: number;
  prevFrom: string;
  prevTo: string | null;
}

export interface WindowTimelineProps {
  groups: TimelineGroup[];
  /** Доступное имя шкалы. */
  ariaLabel: string;
  /** Можно ли двигать полосы. `false` — шкала только для чтения. */
  editable?: boolean;
  /** Можно ли переставлять строки (ручка слева). По умолчанию — как `editable`. */
  reorderable?: boolean;
  /** Шаг перетаскивания. */
  step?: TimelineStep;
  /**
   * Год посадки для относительных окон шаблона: даты вне него подписываются
   * «(предыдущий год)», «(следующий год)». Без него годы показываются, когда
   * окно переходит через границу года.
   */
  relativeYear?: number;
  /** Точный диапазон шкалы; по умолчанию — по окнам работ с запасом. */
  range?: { from: string; to: string };
  /** Пределы перетаскивания; по умолчанию — края шкалы. */
  bounds?: { min?: string; max?: string };
  /** Свёрнутые группы (управляемо). Без него шкала держит их сама. */
  collapsed?: ReadonlySet<string>;
  defaultCollapsed?: Iterable<string>;
  onToggleGroup?: (id: string) => void;
  selectedId?: string | null;
  /** Нажатие на полосу или название работы. */
  onSelect?: (id: string) => void;
  /** Окно изменили: вызывающий превращает это в правку срока. */
  onShift?: (id: string, from: string, to: string | null, info: ShiftInfo) => void;
  /** Строку перенесли в другое место. */
  onReorder?: (move: RowMove) => void;
  /**
   * Пока полосу тащат: окна других работ, которые от неё зависят (чтобы они
   * ехали вместе с якорем, а не прыгали после отпускания). `null` — никого.
   */
  previewShift?: (id: string, from: string, to: string | null, info: ShiftInfo) => Record<string, DayWindow> | null;
  /** Ячейка названия вместо простого текста. */
  renderLabel?: (bar: TimelineBar, group: TimelineGroup) => ReactNode;
  /** Действия строки справа от названия (меню «⋯»). */
  renderRowActions?: (bar: TimelineBar, group: TimelineGroup) => ReactNode;
  /** Действия группы справа от её названия. */
  renderGroupActions?: (group: TimelineGroup) => ReactNode;
}

/* ----------------------------------------------------------------- вспомогательное */

interface PreviewState {
  id: string;
  kind: ShiftKind;
  win: DayWindow;
  deltaDays: number;
  deps: Record<string, DayWindow> | null;
}

interface DragRun {
  id: string;
  kind: ShiftKind;
  pointerId: number;
  startX: number;
  dayPx: number;
  orig: DayWindow;
  win: DayWindow;
  delta: number;
  moved: boolean;
}

interface ReorderState {
  id: string;
  dy: number;
  target: { groupId: string; index: number } | null;
  line: number | null;
}

interface ReorderRun {
  id: string;
  pointerId: number;
  startY: number;
  scrollStart: number;
  scroller: HTMLElement | null;
  lastY: number;
  frame: number;
}

const CLICK_SLOP_PX = 3;
const EDGE_ZONE_PX = 64;

function scrollParentOf(el: HTMLElement | null): HTMLElement | null {
  for (let node = el?.parentElement ?? null; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) return node;
  }
  return null;
}

function winOf(bar: TimelineBar): DayWindow | null {
  return bar.from ? { from: bar.from, to: bar.to } : null;
}

function signed(delta: number): string {
  if (delta === 0) return '0';
  return delta > 0 ? `+${delta}` : `−${Math.abs(delta)}`;
}

/* ---------------------------------------------------------------- компонент */

export function WindowTimeline({
  groups,
  ariaLabel,
  editable = false,
  reorderable,
  step = 'day',
  relativeYear,
  range,
  bounds,
  collapsed: collapsedProp,
  defaultCollapsed,
  onToggleGroup,
  selectedId = null,
  onSelect,
  onShift,
  onReorder,
  previewShift,
  renderLabel,
  renderRowActions,
  renderGroupActions,
}: WindowTimelineProps) {
  const t = useT();
  const canReorder = (reorderable ?? editable) && !!onReorder;

  const [innerCollapsed, setInnerCollapsed] = useState<ReadonlySet<string>>(() => new Set(defaultCollapsed ?? []));
  const collapsed = collapsedProp ?? innerCollapsed;
  const toggleGroup = (id: string) => {
    if (onToggleGroup) onToggleGroup(id);
    else
      setInnerCollapsed((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
  };

  const scale = useMemo(() => {
    const windows: DayWindow[] = [];
    for (const group of groups) for (const bar of group.bars) if (bar.from && !bar.removed) windows.push({ from: bar.from, to: bar.to });
    return buildScale(windows, { range });
  }, [groups, range]);

  const limit = useMemo(
    () => ({
      min: bounds?.min ?? scale?.start,
      max: bounds?.max ?? (scale ? addDays(scale.end, -1) : undefined),
    }),
    [bounds?.min, bounds?.max, scale],
  );

  const barIndex = useMemo(() => {
    const map = new Map<string, { bar: TimelineBar; group: TimelineGroup }>();
    for (const group of groups) for (const bar of group.bars) map.set(bar.id, { bar, group });
    return map;
  }, [groups]);

  /* ---- измерения: положение строк и ширина дорожки нужны для связей */
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const rowEls = useRef(new Map<string, HTMLElement>());
  const [geo, setGeo] = useState<{ width: number; ys: Record<string, number> }>({ width: 0, ys: {} });
  const setRowEl = (key: string) => (el: HTMLElement | null) => {
    if (el) rowEls.current.set(key, el);
    else rowEls.current.delete(key);
  };
  const measure = useCallback(() => {
    const body = bodyRef.current;
    if (!body) return;
    const top = body.getBoundingClientRect().top;
    const ys: Record<string, number> = {};
    rowEls.current.forEach((el, key) => {
      const r = el.getBoundingClientRect();
      ys[key] = Math.round(r.top - top + r.height / 2);
    });
    const width = Math.round(overlayRef.current?.getBoundingClientRect().width ?? 0);
    setGeo((prev) => {
      const keys = Object.keys(ys);
      const same = prev.width === width && keys.length === Object.keys(prev.ys).length && keys.every((k) => prev.ys[k] === ys[k]);
      return same ? prev : { width, ys };
    });
  }, []);
  useLayoutEffect(() => {
    measure();
  });
  useEffect(() => {
    const body = bodyRef.current;
    if (!body || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(body);
    return () => observer.disconnect();
  }, [measure]);

  /* ---- перетаскивание полосы */
  const dragRun = useRef<DragRun | null>(null);
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [announcement, setAnnouncement] = useState('');

  const yearHint = useCallback(
    (iso: string): string | null => {
      if (relativeYear === undefined) return null;
      const year = Number(iso.slice(0, 4));
      if (year < relativeYear) return t(m.yearPrev);
      if (year > relativeYear) return t(m.yearNext);
      return null;
    },
    [relativeYear, t],
  );

  const rangeText = useCallback(
    (win: DayWindow): string => {
      const dateOf = (iso: string) => (relativeYear === undefined && win.to !== null && win.from.slice(0, 4) !== win.to.slice(0, 4) ? dateShort(iso) : dateDay(iso));
      if (win.to === null || win.to === win.from) {
        const hint = yearHint(win.from);
        return hint ? t(m.dateYear, { date: dateOf(win.from), year: hint }) : dateOf(win.from);
      }
      const a = yearHint(win.from);
      const b = yearHint(win.to);
      if (a === b) {
        const text = t(m.range, { from: dateOf(win.from), to: dateOf(win.to) });
        return a ? t(m.rangeYear, { range: text, year: a }) : text;
      }
      return t(m.range, {
        from: a ? t(m.dateYear, { date: dateOf(win.from), year: a }) : dateOf(win.from),
        to: b ? t(m.dateYear, { date: dateOf(win.to), year: b }) : dateOf(win.to),
      });
    },
    [relativeYear, t, yearHint],
  );

  const dragKindOf = (bar: TimelineBar): BarDrag => (editable && onShift && bar.from && !bar.removed ? (bar.drag ?? 'fixed') : 'fixed');

  const cancelDrag = useCallback(() => {
    dragRun.current = null;
    setPreview(null);
  }, []);

  useEffect(() => {
    if (!preview) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') cancelDrag();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [preview, cancelDrag]);

  const infoOf = (kind: ShiftKind, prev: DayWindow, next: DayWindow): ShiftInfo => ({
    kind,
    deltaDays: kind === 'end' && prev.to && next.to ? diffDays(prev.to, next.to) : diffDays(prev.from, next.from),
    prevFrom: prev.from,
    prevTo: prev.to,
  });

  const onBarPointerDown = (event: ReactPointerEvent<HTMLElement>, bar: TimelineBar) => {
    const drag = dragKindOf(bar);
    if (drag === 'fixed' || !scale || event.button !== 0 || !event.isPrimary) return;
    const orig = winOf(bar);
    if (!orig) return;
    const handle = (event.target as HTMLElement).closest<HTMLElement>('[data-handle]')?.dataset.handle;
    const kind: ShiftKind = drag === 'free' && (handle === 'start' || handle === 'end') && orig.to !== null ? handle : 'move';
    const track = event.currentTarget.closest<HTMLElement>('.wt__track');
    const width = track?.getBoundingClientRect().width ?? 0;
    if (!(width > 0)) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRun.current = {
      id: bar.id,
      kind,
      pointerId: event.pointerId,
      startX: event.clientX,
      dayPx: width / scale.totalDays,
      orig,
      win: orig,
      delta: 0,
      moved: false,
    };
  };

  const onBarPointerMove = (event: ReactPointerEvent<HTMLElement>) => {
    const run = dragRun.current;
    if (!run || run.pointerId !== event.pointerId) return;
    const dx = event.clientX - run.startX;
    if (!run.moved && Math.abs(dx) < CLICK_SLOP_PX) return;
    run.moved = true;
    const delta = snapDelta(dx, run.dayPx, step);
    const win = dragWindow(run.kind, run.orig, delta, limit);
    if (preview && run.delta === delta && sameWindow(run.win, win)) return;
    run.delta = delta;
    run.win = win;
    const info = infoOf(run.kind, run.orig, win);
    const deps = sameWindow(win, run.orig) || !previewShift ? null : previewShift(run.id, win.from, win.to, info);
    setPreview({ id: run.id, kind: run.kind, win, deltaDays: info.deltaDays, deps });
  };

  const finishDrag = (event: ReactPointerEvent<HTMLElement>, cancelled: boolean) => {
    const run = dragRun.current;
    if (!run || run.pointerId !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    dragRun.current = null;
    setPreview(null);
    if (cancelled) return;
    if (!run.moved) {
      onSelect?.(run.id);
      return;
    }
    if (sameWindow(run.win, run.orig)) return;
    onShift?.(run.id, run.win.from, run.win.to, infoOf(run.kind, run.orig, run.win));
  };

  const onBarKeyDown = (event: ReactKeyboardEvent<HTMLElement>, bar: TimelineBar) => {
    if (event.key === 'Enter' || event.key === ' ') {
      if (onSelect) {
        event.preventDefault();
        onSelect(bar.id);
      }
      return;
    }
    const drag = dragKindOf(bar);
    if (drag === 'fixed' || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) return;
    const orig = winOf(bar);
    if (!orig) return;
    event.preventDefault();
    const sign = event.key === 'ArrowLeft' ? -1 : 1;
    const days = sign * (event.shiftKey ? 7 : 1);
    const kind: ShiftKind = event.altKey && drag === 'free' && orig.to !== null ? 'end' : 'move';
    const next = dragWindow(kind, orig, days, limit);
    if (sameWindow(next, orig)) return;
    onShift?.(bar.id, next.from, next.to, infoOf(kind, orig, next));
    setAnnouncement(t(m.moved, { name: bar.label, range: rangeText(next) }));
  };

  /* ---- перенос строки */
  const reorderRun = useRef<ReorderRun | null>(null);
  const [reorder, setReorder] = useState<ReorderState | null>(null);
  const focusAfter = useRef<string | null>(null);

  const orderOf = useCallback(() => groups.map((g) => ({ id: g.id, ids: g.bars.map((b) => b.id) })), [groups]);

  const targetAt = useCallback(
    (clientY: number, dragId: string): { groupId: string; index: number; line: number } | null => {
      const body = bodyRef.current;
      if (!body || groups.length === 0) return null;
      const bodyTop = body.getBoundingClientRect().top;
      const rect = (key: string) => rowEls.current.get(key)?.getBoundingClientRect() ?? null;
      const head0 = rect(`group:${groups[0]!.id}`);
      let best = { groupId: groups[0]!.id, index: 0, line: (head0?.bottom ?? bodyTop) - bodyTop };
      for (const group of groups) {
        const head = rect(`group:${group.id}`);
        const others = group.bars.filter((b) => b.id !== dragId);
        if (head) {
          if (clientY < head.top + head.height / 2) return best;
          best = { groupId: group.id, index: collapsed.has(group.id) ? others.length : 0, line: head.bottom - bodyTop };
        }
        if (collapsed.has(group.id)) continue;
        for (let k = 0; k < others.length; k += 1) {
          const r = rect(`bar:${others[k]!.id}`);
          if (!r) continue;
          if (clientY < r.top + r.height / 2) return { groupId: group.id, index: k, line: r.top - bodyTop };
          best = { groupId: group.id, index: k + 1, line: r.bottom - bodyTop };
        }
      }
      return best;
    },
    [groups, collapsed],
  );

  const stopReorder = useCallback(() => {
    const run = reorderRun.current;
    if (run) cancelAnimationFrame(run.frame);
    reorderRun.current = null;
    setReorder(null);
  }, []);

  useEffect(() => {
    if (!reorder) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') stopReorder();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [reorder, stopReorder]);

  const updateReorder = useCallback(
    (run: ReorderRun) => {
      const scrolled = run.scroller ? run.scroller.scrollTop - run.scrollStart : window.scrollY - run.scrollStart;
      const target = targetAt(run.lastY, run.id);
      setReorder({ id: run.id, dy: run.lastY - run.startY + scrolled, target: target ? { groupId: target.groupId, index: target.index } : null, line: target?.line ?? null });
    },
    [targetAt],
  );

  const autoScroll = useCallback(
    (run: ReorderRun) => {
      const view = run.scroller ? run.scroller.getBoundingClientRect() : { top: 0, bottom: window.innerHeight };
      let speed = 0;
      if (run.lastY < view.top + EDGE_ZONE_PX) speed = -Math.ceil(((view.top + EDGE_ZONE_PX - run.lastY) / EDGE_ZONE_PX) * 18);
      else if (run.lastY > view.bottom - EDGE_ZONE_PX) speed = Math.ceil(((run.lastY - (view.bottom - EDGE_ZONE_PX)) / EDGE_ZONE_PX) * 18);
      if (speed !== 0) {
        if (run.scroller) run.scroller.scrollTop += speed;
        else window.scrollBy(0, speed);
        updateReorder(run);
      }
      run.frame = requestAnimationFrame(() => autoScroll(run));
    },
    [updateReorder],
  );

  const onGripPointerDown = (event: ReactPointerEvent<HTMLElement>, bar: TimelineBar) => {
    if (!canReorder || event.button !== 0 || !event.isPrimary) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    const scroller = scrollParentOf(bodyRef.current);
    const run: ReorderRun = {
      id: bar.id,
      pointerId: event.pointerId,
      startY: event.clientY,
      scrollStart: scroller ? scroller.scrollTop : window.scrollY,
      scroller,
      lastY: event.clientY,
      frame: 0,
    };
    reorderRun.current = run;
    run.frame = requestAnimationFrame(() => autoScroll(run));
    updateReorder(run);
  };

  const onGripPointerMove = (event: ReactPointerEvent<HTMLElement>) => {
    const run = reorderRun.current;
    if (!run || run.pointerId !== event.pointerId) return;
    run.lastY = event.clientY;
    updateReorder(run);
  };

  const onGripPointerUp = (event: ReactPointerEvent<HTMLElement>, cancelled: boolean) => {
    const run = reorderRun.current;
    if (!run || run.pointerId !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    const target = cancelled ? null : targetAt(run.lastY, run.id);
    stopReorder();
    if (!target) return;
    const from = barIndex.get(run.id)?.group.id;
    if (!from) return;
    const move: RowMove = { id: run.id, fromGroupId: from, toGroupId: target.groupId, index: target.index };
    const before = orderOf();
    if (sameOrder(before, applyRowMove(before, move))) return;
    focusAfter.current = run.id;
    onReorder?.(move);
  };

  const onGripKeyDown = (event: ReactKeyboardEvent<HTMLElement>, bar: TimelineBar) => {
    if (!canReorder || (event.key !== 'ArrowUp' && event.key !== 'ArrowDown')) return;
    event.preventDefault();
    const move = keyboardMove(orderOf(), bar.id, event.key === 'ArrowUp' ? -1 : 1);
    if (!move) return;
    focusAfter.current = bar.id;
    onReorder?.(move);
    const target = groups.find((g) => g.id === move.toGroupId);
    if (target) {
      const total = target.bars.filter((b) => b.id !== bar.id).length + 1;
      setAnnouncement(t(m.reordered, { name: bar.label, group: target.label, position: move.index + 1, total }));
    }
  };

  // Строка могла перейти в другую группу и перемонтироваться: фокус возвращается на её ручку.
  useEffect(() => {
    const id = focusAfter.current;
    if (!id) return;
    focusAfter.current = null;
    bodyRef.current?.querySelector<HTMLElement>(`[data-grip="${CSS.escape(id)}"]`)?.focus();
  }, [groups]);

  useEffect(
    () => () => {
      if (reorderRun.current) cancelAnimationFrame(reorderRun.current.frame);
    },
    [],
  );

  /* ---- отрисовка */
  const effWindow = (bar: TimelineBar): DayWindow | null => {
    if (!bar.from || bar.removed) return null;
    if (preview?.id === bar.id) return preview.win;
    return preview?.deps?.[bar.id] ?? { from: bar.from, to: bar.to };
  };

  const edgeX = (win: DayWindow, edge: 'start' | 'end'): number => {
    if (!scale) return 0;
    const pct = edge === 'start' ? pctOf(scale, win.from) : pctOf(scale, win.to ?? win.from) + (1 / scale.totalDays) * 100;
    return (pct / 100) * geo.width;
  };

  const links = useMemo(() => {
    if (!scale || geo.width <= 0) return [];
    const out: { id: string; d: string; x: number; y: number }[] = [];
    for (const group of groups) {
      if (collapsed.has(group.id)) continue;
      for (const bar of group.bars) {
        if (!bar.link || !bar.from || bar.removed) continue;
        const anchor = barIndex.get(bar.link.toId);
        const dep = effWindow(bar);
        const anchorWin = anchor ? effWindow(anchor.bar) : null;
        if (!anchor || !dep || !anchorWin) continue;
        const ay = geo.ys[`bar:${anchor.bar.id}`] ?? geo.ys[`group:${anchor.group.id}`];
        const dy = geo.ys[`bar:${bar.id}`];
        if (ay === undefined || dy === undefined) continue;
        const ax = edgeX(anchorWin, bar.link.edge);
        const dx = edgeX(dep, 'start');
        out.push({ id: bar.id, d: `M ${ax} ${ay} V ${dy} H ${dx}`, x: dx, y: dy });
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, collapsed, geo, scale, preview, barIndex]);

  const dragging = !!(preview || reorder);
  // На узкой дорожке подписи месяцев не помещаются: показываем каждый N-й, линии остаются все.
  const tickEvery = scale && geo.width > 0 ? Math.max(1, Math.ceil(38 / (geo.width / scale.ticks.length))) : 1;

  return (
    <div className={`wt${dragging ? ' wt--dragging' : ''}${reorder ? ' wt--reordering' : ''}`} role="group" aria-label={ariaLabel} data-editable={editable || undefined}>
      <div className="wt__head" aria-hidden="true">
        <div className="wt__corner" />
        <div className="wt__scale">
          {scale?.ticks.map((tick, i) => (
            <span key={tick.iso} className="wt__tick" style={{ left: `${tick.left}%` }}>
              {i % tickEvery === 0 ? monthShort(new Date(`${tick.iso}T00:00:00Z`)) : null}
              {i % tickEvery === 0 && (tick.month === 1 || i === 0) ? <small className="wt__year">{tick.year}</small> : null}
            </span>
          ))}
        </div>
      </div>

      <div className="wt__body" ref={bodyRef}>
        <div className="wt__overlay" ref={overlayRef} aria-hidden="true">
          {scale?.ticks.slice(1).map((tick) => (
            <span key={tick.iso} className="wt__gridline" style={{ left: `${tick.left}%` }} />
          ))}
          {links.length > 0 ? (
            <svg className="wt__links" width={geo.width} height="100%">
              {links.map((link) => (
                <g key={link.id}>
                  <path d={link.d} className="wt__link" />
                  <circle cx={link.x} cy={link.y} r="3" className="wt__linkdot" />
                </g>
              ))}
            </svg>
          ) : null}
        </div>

        {groups.map((group) => {
          const isCollapsed = collapsed.has(group.id);
          const windows = group.bars.map(effWindow).filter((w): w is DayWindow => w !== null);
          const span =
            scale && windows.length > 0
              ? windows.reduce<DayWindow>((acc, w) => ({ from: minIso(acc.from, w.from), to: maxIso(acc.to ?? acc.from, w.to ?? w.from) }), windows[0]!)
              : null;
          return (
            <Fragment key={group.id}>
              <div className="wt__row wt__row--group" ref={setRowEl(`group:${group.id}`)} data-collapsed={isCollapsed || undefined}>
                <div className="wt__label">
                  <button type="button" className="wt__toggle focus-ring" aria-expanded={!isCollapsed} onClick={() => toggleGroup(group.id)}>
                    <span className="wt__chev">
                      <Icon icon={ChevronRight} size={16} />
                    </span>
                    <span className="wt__groupname">{group.label}</span>
                    {group.summary ? <span className="wt__groupsum">{group.summary}</span> : null}
                  </button>
                  {renderGroupActions ? <span className="wt__actions">{renderGroupActions(group)}</span> : null}
                </div>
                <div className="wt__track">
                  {scale && span ? (
                    <span
                      className="wt__span"
                      style={{ left: `${pctOf(scale, span.from)}%`, width: `${widthPctOf(scale, span)}%` }}
                    />
                  ) : null}
                </div>
              </div>

              {!isCollapsed && group.bars.length === 0 && group.empty ? <div className="wt__empty">{group.empty}</div> : null}

              {!isCollapsed
                ? group.bars.map((bar) => {
                    const win = effWindow(bar);
                    const drag = dragKindOf(bar);
                    const active = preview?.id === bar.id;
                    const text = win ? rangeText(win) : '';
                    const pos = win && scale ? pctOf(scale, win.from) : 0;
                    const width = win && scale ? widthPctOf(scale, win) : 0;
                    const narrow = geo.width > 0 ? (width / 100) * geo.width < 26 : false;
                    const dragged = reorder?.id === bar.id;
                    const centre = pos + width / 2;
                    const tipSide = centre > 80 ? 'end' : centre < 15 ? 'start' : 'mid';
                    const help = drag === 'free' ? m.helpFree : drag === 'whole' ? m.helpWhole : m.helpOpen;
                    const interactive = drag !== 'fixed';
                    return (
                      <div
                        key={bar.id}
                        className="wt__row"
                        ref={setRowEl(`bar:${bar.id}`)}
                        data-bar={bar.id}
                        data-selected={selectedId === bar.id || undefined}
                        data-removed={bar.removed || undefined}
                        data-changed={bar.changed || undefined}
                        data-dragging={dragged || undefined}
                        style={dragged ? { transform: `translateY(${reorder?.dy ?? 0}px)` } : undefined}
                      >
                        <div className="wt__label">
                          {canReorder ? (
                            <button
                              type="button"
                              className="wt__grip focus-ring"
                              data-grip={bar.id}
                              aria-label={t(m.grip, { name: bar.label })}
                              aria-description={t(m.gripHelp)}
                              aria-keyshortcuts="ArrowUp ArrowDown"
                              title={t(m.gripHelp)}
                              onPointerDown={(e) => onGripPointerDown(e, bar)}
                              onPointerMove={onGripPointerMove}
                              onPointerUp={(e) => onGripPointerUp(e, false)}
                              onPointerCancel={(e) => onGripPointerUp(e, true)}
                              onKeyDown={(e) => onGripKeyDown(e, bar)}
                            >
                              <Icon icon={GripVertical} size={16} />
                            </button>
                          ) : null}
                          <button type="button" className="wt__name focus-ring" onClick={() => onSelect?.(bar.id)} disabled={!onSelect} title={bar.label}>
                            {renderLabel ? renderLabel(bar, group) : <span className="wt__text">{bar.label}</span>}
                          </button>
                          {renderRowActions ? <span className="wt__actions">{renderRowActions(bar, group)}</span> : null}
                        </div>
                        <div className="wt__track">
                          {win && scale ? (
                            <span
                              className={[
                                'wt__bar',
                                bar.approximate ? 'wt__bar--approx' : '',
                                bar.optional ? 'wt__bar--optional' : '',
                                bar.link ? 'wt__bar--linked' : '',
                                win.to === null ? 'wt__bar--point' : '',
                                bar.changed ? 'wt__bar--changed' : '',
                                narrow ? 'wt__bar--narrow' : '',
                              ]
                                .filter(Boolean)
                                .join(' ')}
                              style={{ left: `${pos}%`, width: win.to === null ? undefined : `${width}%` }}
                              data-tone={bar.tone && bar.tone !== 'default' ? bar.tone : undefined}
                              data-drag={drag}
                              data-active={active || undefined}
                              data-tip={tipSide}
                              role={onSelect || interactive ? 'button' : undefined}
                              tabIndex={onSelect || interactive ? 0 : undefined}
                              aria-label={t(m.barLabel, { name: bar.label, range: text })}
                              aria-description={t(help)}
                              aria-pressed={selectedId === bar.id ? true : undefined}
                              onClick={interactive ? undefined : () => onSelect?.(bar.id)}
                              onPointerDown={interactive ? (e) => onBarPointerDown(e, bar) : undefined}
                              onPointerMove={interactive ? onBarPointerMove : undefined}
                              onPointerUp={interactive ? (e) => finishDrag(e, false) : undefined}
                              onPointerCancel={interactive ? (e) => finishDrag(e, true) : undefined}
                              onKeyDown={(e) => onBarKeyDown(e, bar)}
                            >
                              {drag === 'free' && win.to !== null && !narrow ? (
                                <>
                                  <span className="wt__handle wt__handle--start" data-handle="start" />
                                  <span className="wt__handle wt__handle--end" data-handle="end" />
                                </>
                              ) : null}
                              <span className="wt__tip" role="presentation">
                                {active ? t(m.tipDelta, { range: text, delta: t(m.deltaDays, { delta: signed(preview?.deltaDays ?? 0) }) }) : text}
                                {bar.hint && !active ? <small>{bar.hint}</small> : null}
                              </span>
                            </span>
                          ) : (
                            <span className="wt__nowindow">{bar.removed ? '' : t(m.noWindow)}</span>
                          )}
                        </div>
                      </div>
                    );
                  })
                : null}
            </Fragment>
          );
        })}

        {reorder?.line != null ? <span className="wt__drop" style={{ top: reorder.line }} aria-hidden="true" /> : null}
      </div>

      <div className="visually-hidden" aria-live="polite" role="status">
        {announcement}
      </div>
    </div>
  );
}
